import {
  type BranchKey,
  STANDARD_BRANCH,
  useFetchConciseBranch,
  useFetchConciseWorkspace,
  type WorkspaceKey,
} from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useNavigationParams } from '../../system/useNavigationParams'
import { useStandardConciseChangesets } from './useStandardConciseChangesets'

export const useConciseChangesets = ({
  branchKey,
  workspaceKey,
  shouldIncludeBaseConciseChangesets,
}: {
  workspaceKey: Optional<WorkspaceKey>
  branchKey: Optional<BranchKey>
  shouldIncludeBaseConciseChangesets: boolean
}) => {
  const { server: fallbackServer } = useNavigationParams()

  const isBranchEnabled =
    !workspaceKey && !!branchKey && branchKey.name !== STANDARD_BRANCH
  const { data: branchChangesets } = useFetchConciseBranch({
    branchName: branchKey?.name,
    server: branchKey?.server,
    shouldIncludeBaseConciseChangesets,
    enabled: isBranchEnabled,
  })

  const isWorkspaceEnabled = !!workspaceKey
  const { data: workspaceChangesets } = useFetchConciseWorkspace({
    workspaceName: workspaceKey?.name,
    server: workspaceKey?.branchKey.server,
    // We always want the workspace's base branch changesets,
    // since we're unable to differentiate immediate base branch changesets from ancestor branch changesets
    shouldIncludeBaseConciseChangesets: true,
    enabled: isWorkspaceEnabled,
  })

  const isStandardBranchEnabled =
    !!branchKey && branchKey.name === STANDARD_BRANCH
  const standardChangesets = useStandardConciseChangesets({
    server: branchKey?.server ?? fallbackServer,
    enabled: isStandardBranchEnabled,
  })

  return useMemo(
    () => [
      ...(isBranchEnabled ? (branchChangesets ?? []) : []),
      ...(isWorkspaceEnabled ? (workspaceChangesets ?? []) : []),
      ...(isStandardBranchEnabled ? (standardChangesets ?? []) : []),
    ],
    [
      branchChangesets,
      workspaceChangesets,
      standardChangesets,
      isBranchEnabled,
      isWorkspaceEnabled,
      isStandardBranchEnabled,
    ],
  )
}
